import React, { MouseEventHandler, ReactNode, useEffect } from 'react';
import cn from 'classnames';
import Button from '@components/Button';
import styles from './UserMessage.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { selectNavBarState } from '@repo/redux/reducers/ui/navBar';
import { PinNavBarButton } from '@layouts/PrimaryLayout/Navigation/NavBarToggle';
import { hideNavBar, pinNavBar, popOutNavBar } from '@repo/redux/actions/ui';
import { selectNavBarWidth } from '@repo/redux/reducers/ui/panelSizing';

export interface MessageButtonProps {
  text: ReactNode;
  onClick: MouseEventHandler;
  className?: string;
}

export const MessageButton = ({ button }: { button: MessageButtonProps }) => (
  <Button className={cn(styles.btn, button.className)} onClick={button.onClick}>
    {button.text}
  </Button>
);

export default function ViewMessage({
  className,
  headline,
  message,
  icon,
  button,
  underHeader,
  canShowNavBarToggle,
}: {
  className?: string;
  headline: ReactNode;
  message: ReactNode;
  icon: ReactNode;
  button?: MessageButtonProps;
  underHeader?: boolean;
  canShowNavBarToggle?: boolean;
}) {
  const dispatch = useDispatch();
  const { status, transitioning } = useSelector(selectNavBarState);
  const navBarWidth = useSelector(selectNavBarWidth);

  const pinNav = () => dispatch(pinNavBar());
  const popOutNav = () => {
    !transitioning && dispatch(popOutNavBar());
  };

  useEffect(() => {
    const hideNavBarOnMouseLeave = (e) => {
      // Width of nav bar - 8px for right padding
      if ((e?.clientX || 0) < navBarWidth - 8) {
        return;
      }

      dispatch(hideNavBar());
    };

    if (status === 'floating') {
      window.addEventListener('mousemove', hideNavBarOnMouseLeave);
      return () => {
        window.removeEventListener('mousemove', hideNavBarOnMouseLeave);
      };
    }
    return () => {};
  }, [status]);

  return (
    <>
      {canShowNavBarToggle && status !== 'pinned' && (
        <PinNavBarButton
          className={styles.pinNavBarButton}
          onClick={transitioning ? undefined : pinNav}
          onMouseEnter={transitioning ? undefined : popOutNav}
        />
      )}
      <div className={cn(styles.container, className, underHeader && styles.underHeader)}>
        <div className={styles.icon}>{icon}</div>
        <h4>{headline}</h4>
        <div className={styles.message}>{message}</div>
        {button && <MessageButton button={button} />}
      </div>
    </>
  );
}
